/**
 * Authentication Context using Firebase
 *
 * Manages authentication state across the application:
 * - Firebase user state
 * - Vellrix user data from backend
 * - Loading states
 * - Authentication actions (login, logout, register)
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import api from '../services/api';

// User role type
export type UserRole = 'viewer' | 'supervisor' | 'admin';

// User data from Vellrix backend
export interface VellrixUser {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_company_admin: boolean;  // DEPRECATED: Use role instead
  role: UserRole;
  company_id: number;
  firebase_uid: string;
  created_at: string;
  last_login: string | null;
  departments: { id: number; name: string }[];
}

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  vellrixUser: VellrixUser | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [vellrixUser, setVellrixUser] = useState<VellrixUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Start as true until first auth check completes
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch Vellrix user data from backend using Firebase token
   */
  const fetchVellrixUser = async (fbUser: FirebaseUser): Promise<void> => {
    try {
      setError(null);
      const token = await fbUser.getIdToken();

      // Call backend to get Vellrix user data
      const response = await api.get('/auth/me-firebase', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setVellrixUser(response.data);
    } catch (err: any) {
      console.error('Error fetching Vellrix user:', err);
      setError(err.response?.data?.detail || 'Failed to fetch user data');
      setVellrixUser(null);
    }
  };

  /**
   * Refresh user data from backend
   */
  const refreshUser = async (): Promise<void> => {
    if (!firebaseUser) {
      return;
    }

    try {
      setLoading(true);
      await fetchVellrixUser(firebaseUser);
    } catch (err) {
      console.error('Error refreshing user:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user from Firebase and clear state
   */
  const logout = async (): Promise<void> => {
    try {
      await auth.signOut();
      setFirebaseUser(null);
      setVellrixUser(null);
      setError(null);
    } catch (err: any) {
      console.error('Error logging out:', err);
      throw new Error('Failed to logout. Please try again.');
    }
  };

  /**
   * Listen to Firebase auth state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        // User is signed in, fetch Vellrix user data
        await fetchVellrixUser(fbUser);
      } else {
        // User is signed out
        setVellrixUser(null);
        setError(null);
      }

      // Initial check complete, stop showing loading screen
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []); // Empty dependency array - only run once on mount

  const value: AuthContextType = {
    firebaseUser,
    vellrixUser,
    loading,
    error,
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
